import { msAPI, manageAPI, createAPIInstance } from "./index.js";
import config from '@/config';

// VPN check function with 3 second timeout
export const checkVPNConnection = async () => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    
    const response = await fetch(config.vpnCheck, {
      method: 'GET',
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    return response.ok;
  } catch (error) {
    return false;
  }
};

export const login = async (username, password, mfaCode = null) => {
  // First try public management API
  const loginData = {
    user_name: username,
    password: password,
  };
  
  if (mfaCode) {
    loginData.mfa_code = mfaCode;
  }
  
  const publicResult = (await manageAPI.post("merchant/login", loginData)).data;

  // If response indicates VPN is required, check VPN and retry with VPN API
  if (publicResult.f === "vpn") {
    const vpnConnected = await checkVPNConnection();
    if (!vpnConnected) {
      throw new Error("VPN connection required for admin access. Please connect to VPN and try again.");
    }
    
    // Switch to VPN endpoints
    const { switchToVPNEndpoints } = await import("./index.js");
    switchToVPNEndpoints();
    
    // Create fresh VPN API instance
    const vpnManageAPI = createAPIInstance(config.vpnManageAPI, true);
    
    // Retry login with VPN API
    const vpnLoginData = {
      user_name: username,
      password: password,
    };
    
    if (mfaCode) {
      vpnLoginData.mfa_code = mfaCode;
    }
    
    const vpnResult = (await vpnManageAPI.post("merchant/login", vpnLoginData)).data;
    
    return vpnResult;
  }
  
  return publicResult;
}

export const loginWithWebAuthn = async (username, password, credential) => {
  // Convert credential rawId to base64 URL-safe encoding (as per backend guide)
  const credentialData = btoa(String.fromCharCode.apply(null, new Uint8Array(credential.rawId)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  
  // First try public management API
  const loginData = {
    user_name: username,
    password: password,
    webauthn_credential: credentialData,
  };
  
  const publicResult = (await manageAPI.post("merchant/login", loginData)).data;

  // If response indicates VPN is required, check VPN and retry with VPN API
  if (publicResult.f === "vpn") {
    const vpnConnected = await checkVPNConnection();
    if (!vpnConnected) {
      throw new Error("VPN connection required for admin access. Please connect to VPN and try again.");
    }
    
    // Switch to VPN endpoints
    const { switchToVPNEndpoints } = await import("./index.js");
    switchToVPNEndpoints();
    
    // Create fresh VPN API instance
    const vpnManageAPI = createAPIInstance(config.vpnManageAPI, true);
    
    const vpnLoginData = {
      user_name: username,
      password: password,
      webauthn_credential: credentialData,
    };
    
    const vpnResult = (await vpnManageAPI.post("merchant/login", vpnLoginData)).data;
    
    return vpnResult;
  }
  
  return publicResult;
}

export const checkSession = async () => {
  try {
    // First try public management API
    const publicResult = (await manageAPI.get("merchant/auth")).data;

    // If response indicates VPN is required, check VPN and retry with VPN API
    if (publicResult.f === "vpn") {
      const vpnConnected = await checkVPNConnection();
      if (!vpnConnected) {
        throw new Error("VPN connection required for admin access. Please connect to VPN and try again.");
      }
      
      // Switch to VPN endpoints
      const { switchToVPNEndpoints } = await import("./index.js");
      switchToVPNEndpoints();
      
      // Create fresh VPN API instance
      const vpnManageAPI = createAPIInstance(config.vpnManageAPI, true);
      
      // Retry session check with VPN API
      const vpnResult = (await vpnManageAPI.get("merchant/auth")).data;
      
      return vpnResult;
    }
    
    return publicResult;
  } catch (error) {
    // If session check fails, return null to indicate no valid session
    // This will cause the app to show the login screen without error messages
    return null;
  }
}

export const getMerchants = async (full=false) => {
  return (await msAPI.get("merchant")).data;
}

export const getStripeBannerToken = async () => {
  return (await msAPI.get("merchant/stripe-token/banner")).data;
}

export const startStripeOnboarding = async () => {
  return (await msAPI.get("merchant/stripe-token/onboarding")).data;
}

export const getStripePayoutToken = async() => {
  return (await msAPI.get("merchant/stripe-token/payout")).data;
}

export const getStripeDocumentToken = async() => {
  return (await msAPI.get("merchant/stripe-token/document")).data;
}

export const getStripeToken = async(tokenType) => {
  return (await msAPI.get(`merchant/stripe-token/${tokenType}`)).data;
}

export const setupAccount = async (payload) => {
  return (await msAPI.post(`merchant/account/setup`, payload)).data;
};

export const updateStoreHours = async (payload) => {
  return (await msAPI.patch(`merchant/store/hours`, payload)).data;
}

export const updateMerchantUser = async(payload) => {
  return (await msAPI.patch(`merchant/user`, payload)).data;
}

export const updateSellerInfo = async(payload) => {
  return (await msAPI.patch(`merchant/seller`, payload)).data;
}

export const updateMothershipInfo = async (payload) => {
  return (await msAPI.patch(`merchant/mothership`, payload)).data;
}

export const uploadFile = async (file, fileType = "") => {
  const formData = new FormData();
  formData.append("file", file);
  let url = `merchant/file`;
  if(fileType){
    url = `merchant/file/${fileType}`;
  }
  return (await msAPI.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  })).data;
}

export const forgotPassword = async (email) => {
  return (await msAPI.post(`merchant/forgot-password`, {
    email,
  })).data;
}

export const updatePassword = async (token, password) => {
  return (await msAPI.post(`merchant/update-password`, {
    reset: token,
    password,
  })).data;
}

export const setupTOTP = async () => {
  return (await manageAPI.get("merchant/totp/setup")).data;
};

export const verifyTOTP = async (code) => {
  return (await manageAPI.post("merchant/totp/verify", { code })).data;
};

export const generateBackupCodes = async () => {
  return (await manageAPI.post("merchant/backup-codes")).data;
};

export const deleteTOTP = async () => {
  return (await manageAPI.delete("merchant/totp")).data;
};

// WebAuthn API functions
export const beginWebAuthnRegistration = async () => {
  return (await manageAPI.post("merchant/user/webauthn/register/begin", {})).data;
};

export const completeWebAuthnRegistration = async (credential, name) => {
  // Convert credential to base64 - the API expects the entire credential object as base64
  const credentialData = btoa(JSON.stringify({
    id: Array.from(new Uint8Array(credential.id)),
    rawId: Array.from(new Uint8Array(credential.rawId)),
    type: credential.type,
    response: {
      attestationObject: Array.from(new Uint8Array(credential.response.attestationObject)),
      clientDataJSON: Array.from(new Uint8Array(credential.response.clientDataJSON))
    }
  }));
  
  return (await manageAPI.post("merchant/user/webauthn/register/complete", {
    name: name,
    credential: credentialData
  })).data;
};

export const deleteWebAuthnCredential = async (internalId) => {
  return (await manageAPI.delete(`merchant/user/webauthn/credentials/${internalId}`)).data;
};

// WebAuthn Authentication for Login
export const beginWebAuthnAuthentication = async (email) => {
  return (await manageAPI.post("merchant/user/webauthn/authenticate/begin", {
    email: email
  })).data;
};
